import { buildChartSeries } from '../MetricChart/buildChartSeries'
import { getTimeSeriesSummary } from '../MetricChart/getTimeSeriesSummary'
import { MetricUnit, ViewMode } from '../MetricChart/types'
import type { MetricSeries } from '../MetricChart/types'
import { formatDateRange, formatNumber } from '../../utils'
import type { CaptureChartOptions } from '../../chartToImage'

import type { NarrativeView } from './narrativeViews'

export interface MetricsSnapshotDeps {
  /** Raw metrics plus derived series (e.g. followers-net-new). */
  metricsWithDerived: MetricSeries[]
  channelName?: string
  channelService?: string
  channelAvatarUrl?: string
  startDate: Date
  endDate: Date
  /** Benchmark view charts a fixed window that ignores the date picker. */
  benchmarkStartDate?: Date
  benchmarkEndDate?: Date
  loading: boolean
  hasError: boolean
  refreshing: boolean
  hasNeverSynced: boolean
  hasChannel: boolean
}

export interface MetricsViewSnapshot {
  options: CaptureChartOptions
  /** False when the tab has no usable (or threshold-gated) data — skip it. */
  available: boolean
}

/**
 * Builds the capture options + availability for one Metrics tab, independent of
 * which tab is on screen. Pure so the bulk download can capture every available
 * view. Mirrors the on-screen derivations in MetricsNarrativeChart; the "Posts"
 * tab captures the Posts column only (Followers/Reach/Views have their own tabs).
 */
export function buildMetricsViewSnapshot(
  view: NarrativeView,
  deps: MetricsSnapshotDeps,
): MetricsViewSnapshot {
  const {
    metricsWithDerived,
    channelName,
    channelService,
    channelAvatarUrl,
    startDate,
    endDate,
    benchmarkStartDate,
    benchmarkEndDate,
    loading,
    hasError,
    refreshing,
    hasNeverSynced,
    hasChannel,
  } = deps

  const keys = view.key === 'posts' ? ['posts'] : view.metricKeys
  const visibleMetrics = keys
    .map((key) => metricsWithDerived.find((metric) => metric.key === key))
    .filter((metric): metric is MetricSeries => metric != null)

  const isContentImpactView = view.key === 'content-impact'
  const isBenchmarkView = view.key === 'follower-growth-rate'
  const isColumnView =
    !isContentImpactView &&
    visibleMetrics.length === 1 &&
    visibleMetrics[0]?.key === 'posts'

  const chartStartDate = isBenchmarkView
    ? (benchmarkStartDate ?? startDate)
    : startDate
  const chartEndDate = isBenchmarkView ? (benchmarkEndDate ?? endDate) : endDate

  // Bulk snapshots pin the primary period for a predictable package image.
  const chartSeries = buildChartSeries(visibleMetrics, ViewMode.Primary)

  const primaryMetric = visibleMetrics[0]
  const primaryTitle = primaryMetric?.title ?? view.title
  const primarySummaryValue =
    !primaryMetric || loading || hasError
      ? null
      : getTimeSeriesSummary(primaryMetric.data, primaryMetric.summaryMode)
          .latest
  const formattedPrimaryValue =
    primarySummaryValue == null
      ? null
      : primaryMetric?.unit === MetricUnit.Percentage
        ? `${primarySummaryValue.toFixed(2)}%`
        : formatNumber(primarySummaryValue)

  const options: CaptureChartOptions = {
    heading:
      formattedPrimaryValue != null
        ? `${formattedPrimaryValue} ${primaryTitle}`
        : primaryTitle,
    subheading: formatDateRange(chartStartDate, chartEndDate),
    channelName,
    channelService,
    channelAvatarUrl,
    chartMetrics: isColumnView ? visibleMetrics : chartSeries,
    chartStacked: isColumnView,
    chartComposed: isContentImpactView,
    chartStartDate,
    chartEndDate,
    chartMonthly: isBenchmarkView,
  }

  // Per-view availability, mirroring the on-screen empty/threshold gates, so
  // empty or threshold-gated tabs are dropped from the bulk download.
  const hasAnyData = chartSeries.some((series) =>
    series.data.some((point) => point.value != null),
  )
  const hasMeaningfulData = visibleMetrics.some((metric) => {
    const zeroIsMeaningful =
      metric.key === 'followers' || metric.key === 'followers-net-new'
    return metric.data.some(
      (point) => point.value != null && (zeroIsMeaningful || point.value !== 0),
    )
  })
  const showChartEmptyState =
    hasNeverSynced || !hasAnyData || (!isBenchmarkView && !hasMeaningfulData)
  const isThresholdGatedView =
    visibleMetrics.length > 0 &&
    visibleMetrics.every(
      (metric) => metric.key === 'reach' || metric.key === 'views',
    )

  const available =
    hasChannel &&
    !loading &&
    !hasError &&
    !refreshing &&
    visibleMetrics.length > 0 &&
    !showChartEmptyState &&
    !isThresholdGatedView

  return { options, available }
}
